import { useEffect, useState } from 'react'
import { IconButton, TableCell, TableRow, useTheme, Typography, Box, Tooltip } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import PublishIcon from '@mui/icons-material/Publish'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'

import { TextCell } from './text-cell.component'
import { BodyRowCollapse } from './collapsed'
import { EScanAccountUserRoles, EScanStatus, EUserRoles, IScanRespDto } from 'src/types'
import { createProductQRLink, getTableTimestamp } from 'src/common/utils'
import {
  useCancelScanRemove,
  useDeleteScan,
  useItemsRemoveConfirm,
  useMe,
  useNotification,
  usePushScanToBc,
  useUpdateScanStatus,
} from 'src/hooks'

import { useStyles } from './styles'
import { ChevronDownIcon } from 'src/assets/icons/v2/ChevronDownIcon'
import { ChevronUpIcon } from 'src/assets/icons/v2/ChevronUpIcon'
import { TableRelationIcon } from 'src/components/table-relation-icon'
import { ItemWithPopover } from 'src/components/item-with-popover'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { AppEvents, FONTS } from 'src/constants'
import { DEFAULT_LANGUAGE_CODE } from 'src/components/languages/langauges-list/constants'
import { ExternalLink } from 'src/assets/icons/v2/ExternalLink'
import { TextTag } from 'src/components/text-tag'
import { getColorOrNameByScanStatus } from 'src/common/utils/get-color-or name-by-scan-status'
import { ScanRecordIcon } from 'src/components/scanners/scans/scan-record-icon'
import { TrashTimeoutIcon } from 'src/assets/icons'

interface IProps {
  item: IScanRespDto
  isScaner: boolean
  isOpen: boolean
  userRole: EScanAccountUserRoles
  currentUserRoleAndId: {
    id: string
    roles: string[]
  } | null
}

export const BodyRow = ({
  item,
  isScaner,
  isOpen = false,
  userRole,
  currentUserRoleAndId,
}: IProps) => {
  const [itemStatus, setItemStatus] = useState<EScanStatus>(item.status)
  const [isPushDisabled, setPushIsDisabled] = useState<boolean>(false)

  const [t, i18n] = useTranslation()
  const [toggle, setToggle] = useState(isOpen)
  const { mutate: updateScanStatus, isLoading: isUpdatingScanStatus } = useUpdateScanStatus()
  const { mutate: pushScanToBc, isLoading: isPushing } = usePushScanToBc()

  const { mutateAsync: deleteScan, isLoading: isDeleting } = useDeleteScan()
  const { notify } = useNotification()
  const { mutateAsync: cancelScanRemove } = useCancelScanRemove()
  const { setRemoveConfirm } = useItemsRemoveConfirm()

  const { data: curUser } = useMe()
  const theme = useTheme()
  const classes = useStyles()

  useEffect(() => {
    setToggle(isOpen)
  }, [isOpen])

  useEffect(() => {
    setItemStatus(item.status)
  }, [item.status])

  const handleCollapse = () => {
    setToggle((toggle) => !toggle)
  }

  const handleDeleteItem = async () => {
    if (!item || !item.id) return

    setRemoveConfirm({
      handleConfirm: async () => {
        const resp = await deleteScan(item.id)

        if (!resp) return

        notify({
          message: t('items-delete-notification.v'),
          icon: <TrashTimeoutIcon />,
          onButtonClick: () => cancelScanRemove(item.id),
          timeout: 14000,
        })
      },
      itemsTitles: [],
      itemsRelations: [],
      itemsDetails: item
        ? [
            { title: 'Scanner', text: item?.scanner_name || item.scanner_email },
            { title: 'Status', text: getColorOrNameByScanStatus('text', itemStatus) },
          ]
        : [],
      itemsType: 'scan',
    })
  }

  const handlePushToBc = () => {
    setPushIsDisabled(true)
    setItemStatus(EScanStatus.ON_CHAIN)
    pushScanToBc(item.id)
  }

  const handleScanStatus = (status: EScanStatus) => {
    setItemStatus(status)
    updateScanStatus({ id: item.id, status, accountId: item.account?.id })
  }

  const handleCopyClick = (link?: string) => {
    if (!link) {
      return
    }

    const detail = { message: t('clipboard-copied-notification.v') }
    const event = new CustomEvent(AppEvents.HTTP_SUCCESS, { detail })
    document.dispatchEvent(event)

    navigator.clipboard.writeText(link)
  }

  const handlOpen = (link: string) => {
    window.open(link, '_blank')
  }

  return (
    <>
      <TableRow sx={{ '& *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ borderBottom: 'unset' }}>
          <IconButton onClick={handleCollapse}>
            {toggle ? (
              <ChevronUpIcon size={28} color={theme.palette.custom.darkBlue2} />
            ) : (
              <ChevronDownIcon size={28} color={theme.palette.custom.darkBlue2} />
            )}
          </IconButton>
        </TableCell>
        <TableCell align="center" sx={{ borderBottom: 'unset' }}>
          <TextTag
            label={getColorOrNameByScanStatus('text', itemStatus)}
            bgColor={getColorOrNameByScanStatus('color', itemStatus)}
          />
        </TableCell>
        <TableCell align="center" sx={{ borderBottom: 'unset' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ScanRecordIcon type={item.record_type} />
          </Box>
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset' }}>
          <ItemWithPopover
            item={
              <Typography
                sx={{
                  py: '6px',
                  fontSize: 11,
                  color: theme.palette.custom.darkBlue2,
                  fontWeight: FONTS.WEIGHTS.bold,
                  minWidth: '60px',
                }}
              >
                {item.scanner?.name || item.scanner_name || '-'}
              </Typography>
            }
            subtitle={
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '5px',
                }}
              >
                {item.scanner?.email || item.scanner_email}
                <IconButton
                  sx={{
                    padding: '0.375rem',
                  }}
                  onClick={() => {
                    handleCopyClick(item.scanner?.email || item.scanner_email)
                  }}
                >
                  <ContentCopyIcon className={classes.linkIcon} />
                </IconButton>
              </Box>
            }
            // cursor="help"
          />
        </TableCell>
        <TableCell align="center" sx={{ borderBottom: 'unset', maxWidth: '225px' }}>
          {item.account && (
            <Box>
              <TableRelationIcon
                title={item.account?.account_holder || item.account_name}
                iconAlt={item.account?.account_holder || item.account_name}
                icon={item.account.image_small || item.account.image}
                size={40}
                cursor="default"
                resetAvatarMargin
                // avatarPadding="0px 4px 4px"
                // handleClick={handleJourneyClick(product.journey.id, product.account?.id)}
              />
              <TextCell
                withHashSymbol
                primaryAccent
                primary={item.account?.account_holder || item.account_name}
                // secondary={item.product?.code || item.product_code}
                // hideText
              />
            </Box>
          )}
        </TableCell>
        <TableCell align="center" sx={{ borderBottom: 'unset', maxWidth: '225px' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'start',
            }}
          >
            {item.product && (
              <TableRelationIcon
                iconAlt={item.product.name}
                icon={item.product.image_small || item.product.image}
                size={40}
                cursor="default"
                resetAvatarMargin
                title={'#' + item.product?.code || item.product_code}
              />
              // <Avatar
              //   src={item.product.image_small || item.product.image}
              //   alt={item.product.name}
              //   variant="circular"
              // />
            )}
          </Box>
          {item.product && (
            <TextCell
              primaryAccent
              primary={item.product?.name || item.product_name}
              secondary={
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'nowrap',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    color: theme.palette.custom.grey3,
                    fontSize: '9px',
                  }}
                >
                  {t('scan-history-journey-page-text.v')}
                  <IconButton
                    sx={{
                      padding: '2px',
                    }}
                    onClick={() => {
                      handlOpen(
                        createProductQRLink({
                          productId: item.product?.simple_id || '',
                          lng: i18n.language || DEFAULT_LANGUAGE_CODE,
                        }),
                      )
                    }}
                  >
                    <ExternalLink size={12} stroke={theme.palette.custom.grey2} />
                  </IconButton>
                </Box>
              }
              secondarySx={{ color: theme.palette.custom.grey2, fontSize: '10px' }}
            />
          )}
        </TableCell>
        <TableCell sx={{ borderBottom: 'unset', maxWidth: '225px' }}>
          <div className={classes.iconWrapper}>
            {/* {item.step && (
              <Avatar
                src={item.step.image_small || item.step.image}
                alt={item.step.name}
                variant="circular"
              />
            )} */}
            <TextCell
              withHashSymbol
              primaryAccent
              primary={item.step?.name || item.step_name}
              secondary={item.step?.code || item.step_code}
            />
          </div>
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '10px', borderBottom: 'unset' }}>
          {getTableTimestamp({ date: item.created_at, timezone: curUser?.timezone })}
        </TableCell>
        <TableCell align="center" sx={{ fontSize: '10px', borderBottom: 'unset' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
            {(itemStatus === EScanStatus.NEW || itemStatus === EScanStatus.NOT_APPROVED) &&
              userRole !== EScanAccountUserRoles.USER &&
              userRole !== EScanAccountUserRoles.SCANNER && (
                <Tooltip title="Approve Scan">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isUpdatingScanStatus}
                    onClick={handleScanStatus.bind(this, EScanStatus.APPROVED)}
                  >
                    <CheckIcon sx={{ color: 'green' }} />
                  </IconButton>
                </Tooltip>
              )}
            {(itemStatus === EScanStatus.NEW || itemStatus === EScanStatus.APPROVED) &&
              userRole !== EScanAccountUserRoles.USER &&
              userRole !== EScanAccountUserRoles.SCANNER && (
                <Tooltip title="Disapprove Scan">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isUpdatingScanStatus}
                    onClick={handleScanStatus.bind(this, EScanStatus.NOT_APPROVED)}
                  >
                    <ClearIcon sx={{ color: '#E57373' }} />
                  </IconButton>
                </Tooltip>
              )}
            {itemStatus === EScanStatus.APPROVED &&
              userRole !== EScanAccountUserRoles.USER &&
              userRole !== EScanAccountUserRoles.SCANNER && (
                <Tooltip title="Push On-Chain">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isPushing || isPushDisabled}
                    onClick={handlePushToBc}
                  >
                    <PublishIcon sx={{ color: '#6f3cce' }} />
                  </IconButton>
                </Tooltip>
              )}

            {/* Delete button shows for creator of the scan, Admin or Owner of Account*/}
            {itemStatus !== EScanStatus.ON_CHAIN &&
              (userRole === EScanAccountUserRoles.ADMIN ||
                userRole === EScanAccountUserRoles.OWNER ||
                currentUserRoleAndId?.id === item.scanner?.id ||
                currentUserRoleAndId?.roles?.includes(EUserRoles.superadmin)) && (
                <Tooltip title="Delete">
                  <IconButton
                    sx={{
                      padding: '4px',
                    }}
                    disabled={isDeleting}
                    onClick={handleDeleteItem}
                  >
                    <DeleteIcon sx={{ color: theme.palette.custom.warning }} />
                  </IconButton>
                </Tooltip>
              )}
          </Box>
        </TableCell>
        {/* <TableCell align="center" sx={{ borderBottom: 'unset' }}>
          <Button
            onClick={handlePushToBc}
            disabled={
              !item.reviewed ||
              item.account_tx_pushed ||
              isPushing ||
              userRole === EScanAccountUserRoles.USER ||
              userRole === EScanAccountUserRoles.SCANNER
            }
          >
            <HelperWrapper t="scan-push-button-text" ns={ETranslationNs.Scans} />
          </Button>
        </TableCell> */}
      </TableRow>
      <BodyRowCollapse item={item} collapsed={toggle} />
    </>
  )
}
